import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  type Auth,
  type User,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
};

if (
  !firebaseConfig.apiKey ||
  !firebaseConfig.authDomain ||
  !firebaseConfig.projectId
) {
  console.warn(
    'Firebase configuration is incomplete. Please set VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, and VITE_FIREBASE_PROJECT_ID environment variables.'
  );
}

let app: FirebaseApp | undefined;
let auth: Auth | undefined;
let googleProvider: GoogleAuthProvider | undefined;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  googleProvider = new GoogleAuthProvider();
  googleProvider.setCustomParameters({
    prompt: 'select_account',
  });
} catch (error) {
  console.error('Failed to initialize Firebase:', error);
}

function ensureAuth(): Auth {
  if (!auth) {
    throw new Error(
      'Firebase Auth is not initialized. Check your Firebase configuration.'
    );
  }
  return auth;
}

export async function signInWithGoogle() {
  if (!googleProvider) {
    throw new Error(
      'Firebase Auth is not initialized. Check your Firebase configuration.'
    );
  }
  return signInWithPopup(ensureAuth(), googleProvider);
}

export async function signInWithEmail(email: string, password: string) {
  return signInWithEmailAndPassword(ensureAuth(), email, password);
}

export async function signUpWithEmail(email: string, password: string) {
  return createUserWithEmailAndPassword(ensureAuth(), email, password);
}

export async function signOut() {
  return firebaseSignOut(ensureAuth());
}

export async function resetPassword(email: string) {
  return sendPasswordResetEmail(ensureAuth(), email);
}

export async function getIdToken(): Promise<string | null> {
  const authInstance = ensureAuth();
  const user = authInstance.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

export function subscribeToAuthChanges(callback: (user: User | null) => void) {
  return onAuthStateChanged(ensureAuth(), callback);
}

// Export auth for direct access (may be undefined if init failed)
export { auth };
export type { User };
